import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import { CLIENT_TICKET_STATUS_COLORS, client_ticket_chip_label, filter_linkable_tickets } from './task_ticket_links';
import { PRIVATE_LOCK_TITLE, is_raiser_private } from './ticket_visibility';

/**
 * "Client Tickets" subsection of a task card's Linked work panel (refinement A,
 * 2026-09-12). Chips "#<ref> <title> · <status>" for actionable.linkedClientTickets
 * with unlink, plus a "+ link" picker over the client's open client tickets
 * (GET /api/v1/tickets?storeId= — the same list the Client Tickets tab shows,
 * filtered client-side; there is no server search for client tickets).
 *
 * Link / unlink are POSTs returning the actionable view; the chips re-render
 * from that response's linkedClientTickets. Built against the cs-analysis
 * contract before it shipped: an older backend returns no
 * linkedClientTickets (chips read "none linked") and 404s the link routes
 * (the error shows inline; nothing else on the card is affected).
 */
const LinkedClientTickets = ({ store_id, actionable, can_edit = true, on_changed }) => {
    const [items, set_items] = useState(actionable.linkedClientTickets || []);
    const [show_search, set_show_search] = useState(false);
    const [draft, set_draft] = useState('');
    const [candidates, set_candidates] = useState(null); // null = not loaded yet
    const [loading, set_loading] = useState(false);
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);

    // Follow upstream changes (e.g. the row was re-fetched after a PATCH).
    const signature = (actionable.linkedClientTickets || []).map((t) => `${t.id}:${t.status}`).join(',');
    useEffect(() => {
        set_items(actionable.linkedClientTickets || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    // The client's ticket list is loaded once, when the picker first opens.
    useEffect(() => {
        if (!show_search || candidates !== null) return undefined;
        let cancelled = false;
        set_loading(true);
        (async () => {
            try {
                const r = await cs_analysis_fetch({ path: '/api/v1/tickets', query: { storeId: store_id } });
                if (!cancelled) set_candidates((r && r.tickets) || []);
            } catch (e) {
                if (!cancelled) { set_candidates([]); set_err(e.message || String(e)); }
            } finally {
                if (!cancelled) set_loading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [show_search, candidates, store_id]);

    const apply = (next, fallback) => {
        const list = next && Array.isArray(next.linkedClientTickets) ? next.linkedClientTickets : fallback;
        set_items(list);
        if (on_changed) on_changed(next && typeof next === 'object' ? { ...next, linkedClientTickets: list } : { linkedClientTickets: list });
    };

    const link = async (ticket) => {
        set_busy(true);
        set_err(null);
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable.id}/link-client-ticket`,
                method: 'POST',
                body: { ticketId: String(ticket.id) },
            });
            apply(next, [...items.filter((t) => t.id !== ticket.id), { id: ticket.id, ref: ticket.ref, title: ticket.title, status: ticket.status, priority: ticket.priority, module: ticket.module }]);
            set_draft(''); set_show_search(false);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    const unlink = async (ticket) => {
        set_busy(true);
        set_err(null);
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable.id}/unlink-client-ticket`,
                method: 'POST',
                body: { ticketId: String(ticket.id) },
            });
            apply(next, items.filter((t) => t.id !== ticket.id));
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    const matches = show_search ? filter_linkable_tickets(candidates || [], draft, items.map((t) => t.id)).slice(0, 25) : [];

    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#566573', fontWeight: 600 }}>Client Tickets</span>
                {can_edit ? (
                    <button
                        type='button'
                        title={show_search ? 'Cancel' : "Link one of this client's open tickets"}
                        onClick={() => { set_show_search((v) => !v); if (show_search) set_draft(''); }}
                        style={{ border: 'none', background: 'transparent', color: '#2f6fed', cursor: 'pointer', fontSize: 11, padding: 0, fontWeight: 600 }}
                    >
                        {show_search ? '× cancel' : '+ link'}
                    </button>
                ) : null}
            </div>
            {items.length === 0 ? (
                !show_search ? <div style={{ fontSize: 12, color: '#95a5a6', marginBottom: 4 }}>(none linked)</div> : null
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    {items.map((t) => (
                        <span
                            key={t.id}
                            title={`Client ticket CT-${t.ref} · ${t.status || ''}${t.priority ? ' · ' + t.priority : ''}${t.module ? ' · ' + t.module : ''}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, border: '1px solid #e1e6ea', borderRadius: 999, padding: '2px 4px 2px 8px', background: '#f8fafc' }}
                        >
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: CLIENT_TICKET_STATUS_COLORS[t.status] || '#95a5a6', flexShrink: 0 }} />
                            {is_raiser_private(t) ? <span role='img' aria-label='private to raiser' title={PRIVATE_LOCK_TITLE} style={{ fontSize: 11 }}>🔒</span> : null}
                            <a
                                href={`/home/client_tickets?open=${encodeURIComponent(t.id)}`}
                                target='_blank'
                                rel='noreferrer noopener'
                                title='Open this client ticket in a new tab'
                                style={{ color: '#2f6fed', textDecoration: 'none' }}
                            >
                                {client_ticket_chip_label(t)} <span style={{ fontSize: 10 }}>↗</span>
                            </a>
                            {can_edit ? (
                                <button
                                    type='button'
                                    title={`Unlink client ticket CT-${t.ref}`}
                                    disabled={busy}
                                    onClick={() => unlink(t)}
                                    style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: 13, padding: '0 4px', lineHeight: 1 }}
                                >
                                    ✕
                                </button>
                            ) : null}
                        </span>
                    ))}
                </div>
            )}
            {can_edit && show_search ? (
                <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Input
                            bsSize='sm'
                            value={draft}
                            placeholder='Filter open client tickets (title / CT-ref / module)…'
                            autoFocus
                            onChange={(e) => set_draft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Escape') { set_show_search(false); set_draft(''); } }}
                            disabled={busy}
                            style={{ maxWidth: 320, fontSize: 12 }}
                        />
                        {loading ? <span style={{ fontSize: 11, color: '#95a5a6' }}>…</span> : null}
                    </div>
                    <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, minWidth: 320, maxWidth: 460, maxHeight: 240, overflowY: 'auto', background: '#fff', border: '1px solid #d7dce3', borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.1)', marginTop: 2 }}>
                        {matches.length === 0 ? (
                            <div style={{ padding: '6px 10px', fontSize: 12, color: '#95a5a6' }}>
                                {loading || candidates === null ? 'Loading…' : draft.trim() ? 'No open client ticket matches that filter.' : 'No open client tickets to link.'}
                            </div>
                        ) : matches.map((t) => (
                            <button
                                key={t.id}
                                type='button'
                                onClick={() => link(t)}
                                disabled={busy}
                                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #f0f2f5', background: '#fff', padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                            >
                                <div style={{ fontWeight: 600, color: '#2c3e50' }}>CT-{t.ref} {t.title}</div>
                                <div style={{ color: '#7f8c8d', fontSize: 11 }}>
                                    {t.status}{t.priority ? ` · ${t.priority}` : ''}{t.module ? ` · ${t.module}` : ''}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}
            {err ? <div style={{ color: '#c0392b', fontSize: 11, marginTop: 4 }}>{err}</div> : null}
        </div>
    );
};

export default LinkedClientTickets;
